<template>
  <section id="d3-finance">
    <v-container fluid py-0 pr-0>
      <v-layout class="scrolly-side">
        <!-- step container -->
        <v-flex xs3 class="scrolly__text">
          <v-layout wrap justify-center align-start class="step">
            <div class="text-block" data-step="4-1" />
          </v-layout>

          <v-layout align-start class="step">
            <div class="text-block" data-step="4-1-1">
              <v-container>
                <h2 class="headline mb-3">Sounds like an expensive endeavour. How is China doing it?</h2>

                <p class="body-2">
                  The first question that arises with regards to the Belt and Road is—how is it being funded? China may be one of the richest nations in the world, but even their resources are limited. It is estimated that the entire BRI will cost between <a href="http://www.scmp.com/special-reports/business/topics/special-report-belt-and-road/article/2112978/cost-funding-belt-and" target="_blank">US$4 trillion</a> to <a href="http://www.chinanews.com/cj/2017/05-13/8222915.shtml" target="_blank">US$10 trillion.</a>
                </p>
              </v-container>
            </div>
          </v-layout>

          <v-layout align-start class="step">
            <div class="text-block" data-step="4-2">
              <v-container>
                <p class="body-2">
                  China has built a comprehensive financial architecture to guarantee the BRI’s success to the best of its abilities. But Chinese banks and institutions alone will not be able to fund all the projects under the BRI. Private capital plays an important role, and banks all around the world are hopping onto the bandwagon, spreading their operations into countries within the Belt and Road corridors. This will have profound consequences for the global financial system.
                </p>
              </v-container>
            </div>
          </v-layout>

          <v-layout align-start class="step">
            <div class="text-block" data-step="4-2-1">
              <v-container>
                <h2 class="headline mb-3">Changing the game</h2>

                <p class="body-2">Of honourable mention is the Asian Infrastructure Investment Bank (AIIB).</p>

                <p class="body-2">
                  The AIIB is one of the key institutions driving the BRI. It is a multilateral development bank with 56 members states. Thus far, it has funded infrastructure developments in Bangladesh, Myanmar, Pakistan, and Tajikistan.
                </p>
              </v-container>
            </div>
          </v-layout>

          <v-layout align-start class="step">
            <div class="text-block" data-step="4-2-2">
              <v-container>
                <p class="body-2">
                  Since World War II, the global financial landscape has been dominated by the Bretton Woods institutions, namely the World Bank, and the International Monetary Fund, and later the Asian Development Bank. Despite being the second largest economy in the world, <span class="font-weight-bold" style="color: #F16053;">China’s voting power</span> in these institutions do not reflect its economic might. There have been many naysayers and critics of the AIIB, but one can say that China is merely taking corrective action to balance out the scales.
                </p>
              </v-container>
            </div>
          </v-layout>

          <v-layout align-center class="step">
            <div class="text-block" data-step="4-2-2">
              <v-container>
                <p class="body-2">
                  There is a misconception that AIIB is an instrument of Beijing’s policymakers. China has repeatedly emphasised the contrary and added that it invests in infrastructural developments throughout the concerned regions. For example, one of the BRI’s loudest opponents is India, but they are also among the <a href="https://www.forbes.com/sites/salvatorebabones/2018/01/16/chinas-aiib-expected-to-lend-10-15b-a-ye">largest beneficiaries of AIIB.</a>
                </p>
              </v-container>
            </div>
          </v-layout>

        </v-flex>

        <!-- sticky container -->
        <v-flex xs9 class="scrolly__sticky">
          <!-- d3 components -->
          <component :is="renderD3" />
        </v-flex>
      </v-layout>
    </v-container>
  </section>
</template>

<script>
import * as d3 from 'd3';
import scrollama from 'scrollama';

import { mapGetters } from 'vuex';

// components
import D3Clusters from '@/components/controllers/D3Clusters.vue';
import D3BarChart from '@/components/controllers/D3BarChart.vue';

export default {
  name: 'SectionD3',
  components: {
    D3Clusters,
    D3BarChart,
  },
  mounted() {
    this.initScrollama();
  },
  computed: {
    ...mapGetters('view', ['currentPage', 'step']),
    renderD3() {
      if (this.currentPage === 4) {
        return 'D3Clusters';
      } else if (this.currentPage === 4.2) {
        return 'D3BarChart';
      }

      return '';
    },
  },
  methods: {
    initScrollama() {
      // instantiate scrollama
      const scrollerD3Finance = scrollama();

      // setup scrollama
      scrollerD3Finance
        .setup({
          container: '#d3-finance',
          graphic: '#d3-finance .scrolly__sticky',
          step: '#d3-finance .scrolly__text .step', // required
          offset: 64 / window.innerHeight, // after navbar height
        })
        .onStepEnter(this.handleStepEnter)
        .onStepExit(this.handleStepExit)
        .onContainerEnter(this.handleContainerEnter)
        .onContainerExit(this.handleContainerExit);
    },
    handleStepEnter(res) {
      // get data-step
      const currentStep = d3
        .select(res.element)
        .select('.text-block')
        .attr('data-step');

      // update view state
      this.$store.dispatch('view/updateStep', currentStep);

      // handle page state
      this.handlePageState(res.direction, currentStep);
    },
    handleStepExit() {},
    handleContainerEnter() {},
    handleContainerExit() {},
    handlePageState(direction, step) {
      if (direction === 'down') {
        switch (step) {
          case '4-1':
            // update view state
            this.$store.dispatch('view/updatePage', 4);
            break;

          case '4-2':
            // update view state
            this.$store.dispatch('view/updatePage', 4.2);
            break;

          default:
            break;
        }
      } else if (direction === 'up') {
        switch (step) {
          case '4-1-1':
            // update view state
            this.$store.dispatch('view/updatePage', 4);
            break;

          case '4-2-2':
            // update view state
            this.$store.dispatch('view/updatePage', 4.2);
            break;

          default:
            break;
        }
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
#d3-finance {
  position: relative;
  background-color: #F8F4EE;

  .scrolly__text {
    .step {
      .text-block {
        background-color: #fff;
      }
    }
  }
}
</style>
